<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert, useTrack } from 'dashboard/composables';
import { CONTACTS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import Modal from 'dashboard/components/Modal.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';

// SiteUp (Int-F4 / G6): mesclagem de contatos duplicados pelo mesmo
// waha_lid (nono dígito BR). Fluxo em 2 passos — nunca 1 clique. Reusa a
// action Vuex contacts/merge (AccountActionsAPI → ContactMergeAction).
// Mockup aprovado: artifact ffb42a43 (tela 2).
const props = defineProps({
  group: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['merged']);
const show = defineModel('show', { type: Boolean, default: false });

const { t } = useI18n();
const { isWorkspaceV2 } = useWorkspaceV2();
const modalRef = ref(null);
watch(
  show,
  async visible => {
    if (!isWorkspaceV2.value) return;
    await nextTick();
    if (visible) modalRef.value?.open();
    else modalRef.value?.close();
  },
  { immediate: true }
);
const store = useStore();
const { accountId } = useAccount();

const STEP_COMPARE = 1;
const STEP_CONFIRM = 2;
const step = ref(STEP_COMPARE);
const isMerging = ref(false);

const selectedBaseId = ref(props.group.suggested_base_id);

watch(
  () => props.group?.suggested_base_id,
  id => {
    if (id != null) selectedBaseId.value = id;
  }
);

const contactById = id => props.group.contacts.find(c => c.id === id);

const baseContact = computed(() => contactById(selectedBaseId.value));
const mergeeContacts = computed(
  () => props.group.contacts.filter(c => c.id !== selectedBaseId.value) || []
);
const primaryMergee = computed(() => mergeeContacts.value[0] || null);

const isSuggested = id => id === props.group.suggested_base_id;

const mergeeConversationsCount = computed(() =>
  mergeeContacts.value.reduce(
    (sum, contact) => sum + (contact.conversations_count || 0),
    0
  )
);

const mergeeNames = computed(() =>
  mergeeContacts.value.map(contact => contact.name).filter(Boolean)
);

// Heurística simples pro aviso "mesmo número com/sem o nono dígito" —
// só informativo, não afeta a mesclagem em si.
const phonesDifferByNinthDigit = computed(() => {
  if (props.group.contacts.length < 2) return false;
  const digitsOf = phone => (phone || '').replace(/\D/g, '');
  const a = digitsOf(props.group.contacts[0]?.phone_number);
  const b = digitsOf(props.group.contacts[1]?.phone_number);
  if (!a || !b || Math.abs(a.length - b.length) !== 1) return false;
  const [shorter, longer] = a.length < b.length ? [a, b] : [b, a];
  return (
    longer.slice(0, 2) === shorter.slice(0, 2) &&
    longer[2] === '9' &&
    longer.slice(3) === shorter.slice(2)
  );
});

const contactUrl = id => `/app/accounts/${accountId.value}/contacts/${id}`;

const goToConfirm = () => {
  if (!baseContact.value || !mergeeContacts.value.length) return;
  step.value = STEP_CONFIRM;
};

const goBackToCompare = () => {
  step.value = STEP_COMPARE;
};

const close = () => {
  if (isMerging.value) return;
  show.value = false;
  step.value = STEP_COMPARE;
};

const confirmMerge = async () => {
  if (isMerging.value || !baseContact.value || !mergeeContacts.value.length) {
    return;
  }
  isMerging.value = true;
  try {
    // Um ContactMergeAction por par: principal absorve cada duplicata.
    // Ordem estável (mais conversas primeiro) reduz risco de conflito de
    // atributo sobrescrito em cascata. Sequencial de propósito (não
    // Promise.all) — o merge destrutivo não pode rodar em paralelo.
    const ordered = [...mergeeContacts.value].sort(
      (a, b) => (b.conversations_count || 0) - (a.conversations_count || 0)
    );
    await ordered.reduce(async (previous, mergee) => {
      await previous;
      return store.dispatch('contacts/merge', {
        childId: mergee.id,
        parentId: baseContact.value.id,
      });
    }, Promise.resolve());
    useTrack(CONTACTS_EVENTS.MERGED_CONTACTS, { source: 'waha_lid_duplicate' });
    useAlert(t('CONVERSATION_SIDEBAR.WAHA_INFO.MERGE.SUCCESS'));
    emit('merged', {
      baseId: baseContact.value.id,
      mergeeIds: ordered.map(c => c.id),
      wahaLid: props.group.waha_lid,
    });
    isMerging.value = false;
    close();
  } catch (error) {
    useAlert(t('CONVERSATION_SIDEBAR.WAHA_INFO.MERGE.ERROR'));
  } finally {
    isMerging.value = false;
  }
};
</script>

<template>
  <component
    :is="isWorkspaceV2 ? Dialog : Modal"
    ref="modalRef"
    v-model:show="show"
    :on-close="close"
    size="medium"
    :title="
      step === STEP_COMPARE
        ? t('CONVERSATION_SIDEBAR.WAHA_INFO.MERGE.TITLE')
        : t('CONVERSATION_SIDEBAR.WAHA_INFO.MERGE.CONFIRM_TITLE')
    "
    :show-cancel-button="false"
    :show-confirm-button="false"
    confirm-before-close
    :is-loading="isMerging"
    @request-close="close"
    @close="close"
  >
    <div
      v-if="step === STEP_COMPARE"
      class="flex flex-col gap-5 p-6"
      :class="isWorkspaceV2 ? '!p-0 [&_button]:!rounded-xl' : ''"
      data-testid="waha-merge-step-compare"
    >
      <div>
        <h2 v-if="!isWorkspaceV2" class="text-lg font-semibold text-n-slate-12">
          {{ t('CONVERSATION_SIDEBAR.WAHA_INFO.MERGE.TITLE') }}
        </h2>
        <p class="mt-1 text-sm text-n-slate-11">
          {{
            t('CONVERSATION_SIDEBAR.WAHA_INFO.MERGE.LID_INTRO', {
              lid: group.waha_lid,
            })
          }}
        </p>
      </div>

      <div
        class="grid grid-cols-1 gap-3"
        :class="
          group.contacts.length === 2
            ? 'sm:grid-cols-2'
            : 'sm:grid-cols-2 lg:grid-cols-3'
        "
      >
        <button
          v-for="c in group.contacts"
          :key="c.id"
          type="button"
          class="flex flex-col gap-2 rounded-2xl border p-3 text-left transition-colors"
          :class="
            selectedBaseId === c.id
              ? 'border-n-blue-8 bg-n-blue-2'
              : 'border-n-weak hover:bg-n-alpha-2'
          "
          :data-testid="`waha-merge-candidate-${c.id}`"
          @click="selectedBaseId = c.id"
        >
          <div class="flex items-center gap-2">
            <span
              class="flex size-4 items-center justify-center rounded-full border"
              :class="
                selectedBaseId === c.id
                  ? 'border-n-blue-9 bg-n-blue-9'
                  : 'border-n-slate-8'
              "
            />
            <span
              class="text-[11px] font-semibold uppercase tracking-wide text-n-slate-11"
            >
              {{
                selectedBaseId === c.id
                  ? t('CONVERSATION_SIDEBAR.WAHA_INFO.MERGE.PRIMARY_LABEL')
                  : t('CONVERSATION_SIDEBAR.WAHA_INFO.MERGE.MERGEE_LABEL')
              }}
            </span>
            <span
              v-if="isSuggested(c.id)"
              class="ml-auto text-[11px] text-n-blue-11"
            >
              {{ t('CONVERSATION_SIDEBAR.WAHA_INFO.MERGE.SUGGESTED') }}
            </span>
          </div>

          <div class="flex items-center gap-2">
            <div class="text-sm">
              <div class="font-medium text-n-slate-12">{{ c.name }}</div>
              <div class="text-xs text-n-slate-11 tabular-nums">
                {{ c.phone_number || '—' }}
              </div>
            </div>
          </div>

          <dl class="flex flex-col gap-1 text-xs">
            <div class="flex justify-between gap-2">
              <dt class="text-n-slate-11">
                {{ t('CONVERSATION_SIDEBAR.WAHA_INFO.MERGE.CONVERSATIONS') }}
              </dt>
              <dd class="font-medium text-n-slate-12 tabular-nums">
                {{ c.conversations_count }}
              </dd>
            </div>
            <div class="flex justify-between gap-2">
              <dt class="text-n-slate-11">
                {{ t('CONVERSATION_SIDEBAR.WAHA_INFO.MERGE.EMAIL') }}
              </dt>
              <dd class="truncate font-medium text-n-slate-12">
                {{ c.email || '—' }}
              </dd>
            </div>
            <div class="flex justify-between gap-2">
              <dt class="text-n-slate-11">
                {{
                  t('CONVERSATION_SIDEBAR.WAHA_INFO.MERGE.ATTRIBUTES_FILLED')
                }}
              </dt>
              <dd class="font-medium text-n-slate-12 tabular-nums">
                {{ c.custom_attributes_count }}
              </dd>
            </div>
          </dl>

          <div v-if="c.labels?.length" class="flex flex-wrap gap-1">
            <span
              v-for="label in c.labels"
              :key="label"
              class="rounded-full bg-n-alpha-2 px-1.5 py-0.5 text-[11px] text-n-slate-11"
            >
              {{ label }}
            </span>
          </div>

          <a
            :href="contactUrl(c.id)"
            target="_blank"
            rel="noopener noreferrer"
            class="mt-1 inline-flex items-center gap-1 text-xs text-n-blue-11 hover:underline"
            @click.stop
          >
            <span class="i-ph-arrow-square-out size-3" />
            {{ t('CONVERSATION_SIDEBAR.WAHA_INFO.MERGE.OPEN_FULL') }}
          </a>

          <div
            class="mt-1 flex items-center gap-1 text-xs font-medium"
            :class="
              selectedBaseId === c.id ? 'text-n-teal-11' : 'text-n-ruby-11'
            "
          >
            <span
              :class="selectedBaseId === c.id ? 'i-ph-check' : 'i-ph-trash'"
              class="size-3.5"
            />
            {{
              selectedBaseId === c.id
                ? t('CONVERSATION_SIDEBAR.WAHA_INFO.MERGE.FATE_KEEP')
                : t('CONVERSATION_SIDEBAR.WAHA_INFO.MERGE.FATE_GONE')
            }}
          </div>
        </button>
      </div>

      <div
        v-if="phonesDifferByNinthDigit"
        class="flex items-start gap-2 rounded-2xl bg-n-alpha-2 px-3 py-2 text-xs text-n-slate-11"
      >
        <span class="i-ph-warning size-3.5 shrink-0 mt-0.5" />
        <span
          v-html="t('CONVERSATION_SIDEBAR.WAHA_INFO.MERGE.NINTH_DIGIT_NOTE')"
        />
      </div>

      <div class="flex items-center justify-end gap-2">
        <button
          type="button"
          class="min-h-11 rounded-full px-4 py-2 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2"
          @click="close"
        >
          {{ t('CONVERSATION_SIDEBAR.WAHA_INFO.MERGE.CANCEL') }}
        </button>
        <button
          type="button"
          class="min-h-11 rounded-full bg-n-slate-12 px-4 py-2 text-sm font-semibold text-n-background hover:opacity-90 disabled:opacity-50"
          data-testid="waha-merge-continue"
          :disabled="!baseContact || !mergeeContacts.length"
          @click="goToConfirm"
        >
          {{ t('CONVERSATION_SIDEBAR.WAHA_INFO.MERGE.CONTINUE') }}
        </button>
      </div>
    </div>

    <div
      v-else
      class="flex flex-col gap-5 p-6"
      :class="isWorkspaceV2 ? '!p-0 [&_button]:!rounded-xl' : ''"
      data-testid="waha-merge-step-confirm"
    >
      <div>
        <h2 v-if="!isWorkspaceV2" class="text-lg font-semibold text-n-slate-12">
          {{ t('CONVERSATION_SIDEBAR.WAHA_INFO.MERGE.CONFIRM_TITLE') }}
        </h2>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ t('CONVERSATION_SIDEBAR.WAHA_INFO.MERGE.CONFIRM_INTRO') }}
        </p>
      </div>

      <div class="flex items-center justify-center gap-3">
        <div class="flex flex-col items-center gap-1 text-center">
          <div class="text-sm font-medium text-n-slate-12">
            {{
              mergeeNames.length > 1
                ? mergeeNames.join(', ')
                : primaryMergee?.name
            }}
          </div>
          <span
            class="rounded-full bg-n-ruby-3 px-1.5 py-0.5 text-[11px] font-semibold text-n-ruby-11"
          >
            {{ t('CONVERSATION_SIDEBAR.WAHA_INFO.MERGE.FATE_GONE_SHORT') }}
          </span>
        </div>
        <span class="i-ph-arrow-right size-4 shrink-0 text-n-slate-10" />
        <div class="flex flex-col items-center gap-1 text-center">
          <div class="text-sm font-medium text-n-slate-12">
            {{ baseContact?.name }}
          </div>
          <span
            class="rounded-full bg-n-teal-3 px-1.5 py-0.5 text-[11px] font-semibold text-n-teal-11"
          >
            {{ t('CONVERSATION_SIDEBAR.WAHA_INFO.MERGE.FATE_KEEP_SHORT') }}
          </span>
        </div>
      </div>

      <ul class="flex flex-col gap-2.5 text-sm text-n-slate-12">
        <li class="flex items-start gap-2">
          <span
            class="i-ph-chat-circle size-4 shrink-0 mt-0.5 text-n-slate-10"
          />
          <span
            v-html="
              t('CONVERSATION_SIDEBAR.WAHA_INFO.MERGE.EFFECT_CONVERSATIONS', {
                count: mergeeConversationsCount,
                target:
                  mergeeNames.length > 1
                    ? mergeeNames.join(', ')
                    : primaryMergee?.name,
              })
            "
          />
        </li>
        <li class="flex items-start gap-2">
          <span class="i-ph-chat-text size-4 shrink-0 mt-0.5 text-n-slate-10" />
          {{ t('CONVERSATION_SIDEBAR.WAHA_INFO.MERGE.EFFECT_MESSAGES') }}
        </li>
        <li class="flex items-start gap-2">
          <span class="i-ph-tag size-4 shrink-0 mt-0.5 text-n-slate-10" />
          {{ t('CONVERSATION_SIDEBAR.WAHA_INFO.MERGE.EFFECT_LABELS') }}
        </li>
        <li class="flex items-start gap-2">
          <span
            class="i-ph-list-bullets size-4 shrink-0 mt-0.5 text-n-slate-10"
          />
          {{ t('CONVERSATION_SIDEBAR.WAHA_INFO.MERGE.EFFECT_ATTRIBUTES') }}
        </li>
        <li class="flex items-start gap-2">
          <span class="i-ph-trash size-4 shrink-0 mt-0.5 text-n-slate-10" />
          <span
            v-html="
              t('CONVERSATION_SIDEBAR.WAHA_INFO.MERGE.EFFECT_DELETE', {
                name:
                  mergeeNames.length > 1
                    ? mergeeNames.join(', ')
                    : primaryMergee?.name,
              })
            "
          />
        </li>
        <li class="flex items-start gap-2">
          <span
            class="i-ph-shield-check size-4 shrink-0 mt-0.5 text-n-slate-10"
          />
          <b>{{
            t('CONVERSATION_SIDEBAR.WAHA_INFO.MERGE.EFFECT_NOTHING_SENT')
          }}</b>
        </li>
      </ul>

      <div
        class="flex items-start gap-2 rounded-2xl border border-n-amber-6 bg-n-amber-2 px-3 py-2 text-xs font-medium text-n-amber-11"
      >
        <span class="i-ph-warning size-3.5 shrink-0 mt-0.5" />
        {{ t('CONVERSATION_SIDEBAR.WAHA_INFO.MERGE.IRREVERSIBLE') }}
      </div>

      <div class="flex flex-wrap items-center justify-end gap-2">
        <button
          type="button"
          class="min-h-11 rounded-full px-4 py-2 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2"
          :disabled="isMerging"
          @click="goBackToCompare"
        >
          {{ t('CONVERSATION_SIDEBAR.WAHA_INFO.MERGE.BACK') }}
        </button>
        <button
          type="button"
          class="min-h-11 rounded-full px-4 py-2 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2"
          :disabled="isMerging"
          @click="close"
        >
          {{ t('CONVERSATION_SIDEBAR.WAHA_INFO.MERGE.CANCEL') }}
        </button>
        <button
          type="button"
          class="min-h-11 rounded-full border border-n-ruby-8 px-4 py-2 text-sm font-semibold text-n-ruby-11 hover:bg-n-ruby-3 disabled:opacity-50"
          data-testid="waha-merge-confirm"
          :disabled="isMerging"
          @click="confirmMerge"
        >
          {{ t('CONVERSATION_SIDEBAR.WAHA_INFO.MERGE.CONFIRM_BUTTON') }}
        </button>
      </div>
    </div>
  </component>
</template>
